import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dealEventLogControllerGetDealEventRecipientsOptions } from '@dealstack/shared/lib/backendClient/@tanstack/react-query.gen';
import { MAX_RECIPIENT_OPTIONS } from '@dealstack/shared/lib/common/business/dealEvent';
import { useDebounceValue } from '@dealstack/frontend/shared/other/react/hooks/useDebounceValue';
import type { AsyncFilterMultiSelectProps } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { dealEventLogRequestOptions } from '@dealstack/frontend/app/pages/Project/DealEventLog/useDealEventLogUnread.ts';
import type { DealEventFilterOption } from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogFilterOptions.utils.ts';

/**
 * Recipient options, shown by name and filtered by address. The endpoint caps the list, so the
 * search term goes to the server — otherwise a deal with more recipients than the cap has some
 * of them permanently unreachable. The list is fetched when the dropdown opens and not cached
 * beyond that, so recipients of emails sent since the page loaded are included.
 */
export const useDealEventRecipients = (dealId: string, enabled: boolean) => {
	const [opened, setOpened] = useState(false);
	const [search, setSearch] = useState('');
	const debouncedSearch = useDebounceValue(search);

	const query = useQuery({
		...dealEventLogControllerGetDealEventRecipientsOptions({
			...dealEventLogRequestOptions(dealId),
			query: { limit: MAX_RECIPIENT_OPTIONS, search: debouncedSearch || undefined },
		}),
		enabled: enabled && opened && !!dealId,
		staleTime: 0,
	});

	const options = useMemo<DealEventFilterOption[]>(
		() =>
			// The address is what the feed filters on; the name is what the list shows, and
			// is only missing when a send was never linked to a person.
			(query.data?.recipients ?? []).map(({ email, name }) => ({
				value: email,
				label: name ?? email,
			})),
		[query.data]
	);

	const async: AsyncFilterMultiSelectProps = {
		serverSide: true,
		onDropdownOpen: () => setOpened(true),
		onSearchChange: setSearch,
		isLoading: query.isFetching,
		hasMore: !!query.data?.hasMore,
	};

	return { options, async };
};
